import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, Title } from "rbx";
import { useApolloClient, useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { ReactSelect, Loader } from "../../../../components";
import {
  GET_REVIEWS,
  GET_USERS,
  UPDATE_ONE_EVALUATION,
  CREATE_ONE_EVALUATION,
  DELETE_ONE_EVALUATION,
} from "../../graphql";
import { useAuth } from "../../../../context";
import capitalizeEveryWord from "../../../../utils/capitalizeEveryWord";
import { deepOmit } from "../../../../utils";

const AssignEvaluationForm = ({ evaluation, reviewId, onComplete }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPreviousAssignments, setLoadingPreviousAssignments] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [users, setUsers] = useState(null);
  const [previouslyAssigned, setPreviouslyAssigned] = useState([]);
  const [previouslyAssignedList, setPreviouslyAssignedList] = useState([]);
  const [previouslyAssignedEmployeeNumbers, setPreviouslyAssignedEmployeeNumbers] = useState([]);

  const client = useApolloClient();

  const auth = useAuth();
  const activeEmployeeNumber = auth.state.user.employeeNumber;

  const isDisabled =
    selectedEmployees?.length === 0 || selectedEmployees?.length > 10;

  const isMultiSelect =
    (evaluation?.title?.includes("Client") &&
      (evaluation?.title?.includes("D1") ||
        evaluation?.title?.includes("D2") ||
        evaluation?.title?.includes("D3"))) ||
    evaluation?.title?.includes("PPA"); // handles D4 and D5

  const [updateOneEvaluation] = useMutation(UPDATE_ONE_EVALUATION, {
    refetchQueries: [
      {
        query: GET_REVIEWS,
        variables: {
          where: {
            evaluations: {
              some: {
                authorEmployeeNumber: {
                  contains: activeEmployeeNumber,
                },
              },
            },
          },
          orderBy: [
            {
              name: "asc",
            },
          ],
          sectionsOrderBy: [{ order: "asc" }],
          questionsOrderBy: [{ order: "asc" }],
        },
        fetchPolicy: "network-only",
        awaitRefetchQueries: true,
      },
    ],
  });

  const [deleteOneEvaluation] = useMutation(DELETE_ONE_EVALUATION, {
    refetchQueries: [
      {
        query: GET_REVIEWS,
        variables: {
          where: {
            evaluations: {
              some: {
                authorEmployeeNumber: {
                  contains: activeEmployeeNumber,
                },
              },
            },
          },
          orderBy: [
            {
              name: "asc",
            },
          ],
          sectionsOrderBy: [{ order: "asc" }],
          questionsOrderBy: [{ order: "asc" }],
        },
        fetchPolicy: "network-only",
        awaitRefetchQueries: true,
      },
    ],
  });

  const [createOneEvaluation] = useMutation(CREATE_ONE_EVALUATION);

  const fetchEmployeeData = async (variables) => {
    const result = await client.query({
      query: GET_USERS,
      variables,
    });

    setUsers(result?.data?.users);
  };

  const fetchPreviouslyAssignedEvaluations = async () => {
    setLoadingPreviousAssignments(true);
    try {
      const { data: reviewData } = await client.query({
        query: GET_REVIEWS,
        variables: {
          where: {
            id: {
              equals: reviewId
            }
          }
        },
        fetchPolicy: 'network-only'
      });

      if (reviewData?.reviews?.[0]) {
        // Find evaluations with the same title that are already assigned (not needing assignment)
        const assignedEvaluations = reviewData.reviews[0].evaluations.filter(
          evalItem =>
            evalItem.title === evaluation.title &&
            !evalItem.needsAssignment &&
            evalItem.id !== evaluation.id &&
            evalItem.authorEmployeeNumber !== evaluation.ownerEmployeeNumber
        );

        const list = assignedEvaluations
          .map((evalItem) => ({
            name: evalItem.formAuthor || evalItem.author,
            employeeNumber: evalItem.formAuthorEmployeeNumber || evalItem.authorEmployeeNumber,
            status: evalItem.status
          }))
          .filter((item) => item.name && item.employeeNumber);

        setPreviouslyAssigned(list.map((item) => item.name));
        setPreviouslyAssignedList(list);
        setPreviouslyAssignedEmployeeNumbers(list.map((item) => item.employeeNumber));
      }
    } catch (error) {
      console.error("Error fetching previously assigned evaluations:", error);
    } finally {
      setLoadingPreviousAssignments(false);
    }
  };

  useEffect(() => {
    if (evaluation) {
      const evaluationGroup = evaluation.title.split("- ")[1];
      const variables =
        evaluationGroup !== "PPA" && evaluationGroup
          ? {
            where: {
              employmentStatus: { equals: "active" },
            },
            orderBy: [
              {
                firstName: "asc",
              },
            ],
          }
          : { where: { employmentStatus: { equals: "active" } } };

      fetchEmployeeData(variables);
      fetchPreviouslyAssignedEvaluations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluation]);

  useEffect(() => {
    if (users && !loadingPreviousAssignments) {
      // added filter condition on to remove former employees
      // changed from a name filter to using employeeNumber
      const usersWithoutCurrentOwner = users?.filter(
        (user) =>
          !previouslyAssignedEmployeeNumbers.includes(user?.employeeNumber) // Exclude previously assigned users
      );

      console.log("Filtering users:", {
        totalUsers: users.length,
        previouslyAssignedNumbers: previouslyAssignedEmployeeNumbers,
        filteredUsers: usersWithoutCurrentOwner.length
      });

      const dropdownOptions = usersWithoutCurrentOwner?.map((user) => ({
        label: capitalizeEveryWord(
          `${user?.firstName?.trim()} ${user?.lastName?.trim()}`
        ),
        value: user?.employeeNumber,
      }));

      const sortedOptions = dropdownOptions.sort((a, b) =>
        a.label.localeCompare(b.label)
      );

      setOptions(sortedOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, previouslyAssignedEmployeeNumbers, loadingPreviousAssignments]);

  const handleChange = (selected, n) => {
    setSelectedEmployees(selected);
  };

  const handleAssignEvaluation = async (e) => {
    // debugger; // Removed for production
    e.preventDefault();

    if (isMultiSelect) {
      // Fetch the latest review data to get fresh evaluation data
      try {
        const { data: freshReviewData } = await client.query({
          query: GET_REVIEWS,
          variables: {
            where: {
              id: {
                equals: reviewId
              }
            }
          },
          fetchPolicy: 'network-only' // Skip cache to get latest data
        });

        if (!freshReviewData?.reviews?.[0]) {
          toast.error("Could not find the review. Please try again.");
          return;
        }

        // Find the current evaluation in the fresh data
        const freshEvaluation = freshReviewData.reviews[0].evaluations.find(
          evalItem => evalItem.id === evaluation.id
        );

        if (!freshEvaluation) {
          toast.error("Could not find the evaluation. Please try again.");
          return;
        }

        // Use the fresh evaluation data instead of the passed-in data
        const evalCopy = freshEvaluation;
        const omitted = deepOmit(evalCopy, ["__typename"]);

        // remove id and reviewId from omitted object
        delete omitted.id;
        delete omitted.reviewId;

        try {
          // Peer review due date: 2 weeks from assignment date
          const twoWeeksFromNow = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();

          await Promise.all(
            selectedEmployees?.map(async (selectedEmployee, index) => {
              const CreateOneEvaluationInput = {
                variables: {
                  data: {
                    ...omitted,
                    author: selectedEmployee?.label.toUpperCase(), // keep in line with other names
                    authorEmployeeNumber: selectedEmployee?.value,
                    formAuthor: selectedEmployee?.label.toUpperCase(),// assign writer one stage earlier
                    formAuthorEmployeeNumber: selectedEmployee?.value,
                    authorHidden: true,
                    needsAssignment: false,
                    dateToCompleteBy: twoWeeksFromNow,
                    review: {
                      connect: {
                        id: reviewId,
                      },
                    },
                    sections: {
                      create: omitted?.sections?.map((section) => ({
                        instructions: section.instructions,
                        title: section.title,
                        questions: {
                          create: section.questions.map((question) => ({
                            answerType: question.answerType,
                            text: question.text,
                            answerOptions: {
                              create: question.answerOptions.map(
                                (answerOption) => ({
                                  name: answerOption.name,
                                  value: answerOption.value,
                                })
                              ),
                            },
                          })),
                        },
                      })),
                    },
                  },
                },
              };

              return createOneEvaluation(CreateOneEvaluationInput);
            })
          );

          const DeleteOneEvaluationInput = {
            variables: {
              where: {
                id: evaluation?.id,
              },
            },
          };

          /*
          try {
            await deleteOneEvaluation(DeleteOneEvaluationInput);
          } catch (deleteError) {
            // Continue even if delete fails
          }
          */

          toast.success("Successfully assigned evaluations");
        } catch (err) {
          toast.error("Error: Please contact support");
        }
      } catch (fetchError) {
        toast.error("Error: Please contact support");
      }
    } else {
      try {
        // Peer review due date: 2 weeks from assignment date
        const twoWeeksFromNow = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();

        const UpdateOneEvaluationInput = {
          variables: {
            data: {
              author: {
                set: selectedEmployees?.label,
              },
              authorEmployeeNumber: {
                set: selectedEmployees?.value,
              },
              authorHidden: {
                set: true,
              },
              needsAssignment: {
                set: false,
              },
              dateToCompleteBy: {
                set: twoWeeksFromNow,
              },
            },
            where: {
              id: evaluation.id,
            },
          },
        };

        await updateOneEvaluation(UpdateOneEvaluationInput);
        toast.success("Successfully assigned evaluations");
      } catch (err) {
        toast.error("Error: Please contact support");
      }
    }

    onComplete();
  };

  const loadingAssignEvaluation = async (e) => {
    setLoading(true);
    await handleAssignEvaluation(e);
  };

  if (loading || loadingPreviousAssignments) {
    return (
      <div style={{ minHeight: "10rem" }}>
        <div className="loader-container">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <form id="assign-evals-form" onSubmit={(e) => loadingAssignEvaluation(e)}>
      <header className="modal-head">
        <div className="modal-head">
          <Title size={5}>Assign Evaluation</Title>
        </div>
        <div className="modal-head">
          <Button.Group hasAddons>
            <Button
              size="small"
              style={{ marginBottom: "0.5rem" }}
              type="button"
              onClick={() => onComplete("cancel")}
            >
              <span>Cancel</span>
            </Button>

            <Button
              color="primary"
              disabled={isDisabled}
              size="small"
              style={{ marginBottom: "0.5rem" }}
              type="submit"
            >
              <span>Save and Close</span>
            </Button>
          </Button.Group>
        </div>
      </header>
      <hr />
      {previouslyAssignedList.length > 0 && (
        <div style={{ marginBottom: "1rem" }}>
          <Title size={6} style={{ marginBottom: "0.5rem" }}>Previously Assigned:</Title>
          <div style={{
            backgroundColor: "#f5f5f5",
            padding: "0.75rem",
            borderRadius: "4px",
            border: "1px solid #dbdbdb"
          }}>
            {previouslyAssignedList.map((item) => {
              const isCompleted = item.status === "COMPLETED";
              return (
                <span
                  key={`${item.name}-${item.employeeNumber}`}
                  style={{
                    display: "inline-block",
                    backgroundColor: isCompleted ? "#2e7d32" : "#c62828",
                    color: "#fff",
                    padding: "0.25rem 0.5rem",
                    marginRight: "0.5rem",
                    marginBottom: "0.25rem",
                    borderRadius: "3px",
                    fontSize: "0.875rem"
                  }}
                >
                  {item.name}
                </span>
              );
            })}
          </div>
        </div>
      )}
      {selectedEmployees?.length > 10 && (
        <span style={{ color: "red" }}>
          Please select no more than 10 assignees
        </span>
      )}
      <div style={{ marginBottom: "1rem" }}>
        <Title size={6} style={{ marginBottom: "0.5rem" }}>
          {previouslyAssigned.length > 0 ? "Assign Additional Employees:" : "Assign Employees:"}
        </Title>
        <ReactSelect
          closeMenuOnSelect={!isMultiSelect}
          isMulti={isMultiSelect}
          name="employees"
          options={
            options &&
            options?.map((option) => ({
              label: option?.label,
              value: option?.value,
            }))
          }
          placeholder="Select Employees"
          value={selectedEmployees}
          onChange={(e, n) => {
            handleChange(e, n);
          }}
        />
      </div>
    </form>
  );
};

AssignEvaluationForm.propTypes = {
  evaluation: PropTypes.object.isRequired,
  reviewId: PropTypes.string.isRequired,
  onComplete: PropTypes.func.isRequired,
};

export default AssignEvaluationForm;
